import React, { useState, useEffect, useCallback } from 'react';
import mammoth from 'mammoth';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Fade
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Article as DocIcon,
  Visibility as PreviewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface ContractDocumentsProps {
  contractId: number;
  projectId: number;
  canWrite: boolean;
  onDocumentCountChange?: (count: number) => void;
}

interface Document {
  document_id: number;
  document_name: string;
  document_type: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  version: number;
  notes: string | null;
  expiry_date: string | null;
  created_at: string;
  uploaded_by_name?: string;
}

const ContractDocuments: React.FC<ContractDocumentsProps> = ({
  contractId,
  projectId,
  canWrite,
  onDocumentCountChange
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wordHtml, setWordHtml] = useState<string | null>(null);

  const [uploadData, setUploadData] = useState({
    document_type: 'contract',
    notes: '',
    expiry_date: ''
  });

  const documentTypes = [
    { value: 'contract', label: 'Contract', icon: '📄', formats: 'pdf, doc, docx' },
    { value: 'amendment', label: 'Amendment', icon: '📝', formats: 'pdf, doc, docx' },
    { value: 'variation', label: 'Variation Order', icon: '🔄', formats: 'pdf, doc, docx, xlsx, xls' },
    { value: 'invoice', label: 'Invoice', icon: '💰', formats: 'pdf, xlsx, xls' },
    { value: 'correspondence', label: 'Correspondence', icon: '📧', formats: 'pdf, doc, docx, msg, eml' },
    { value: 'insurance', label: 'Insurance', icon: '🛡️', formats: 'pdf, jpg, jpeg, png' },
    { value: 'bond', label: 'Bond/Guarantee', icon: '🔐', formats: 'pdf, jpg, jpeg, png' },
    { value: 'other', label: 'Other', icon: '📎', formats: 'pdf, doc, docx, xlsx, xls, jpg, jpeg, png, gif, zip, rar' }
  ];

  const fetchDocuments = useCallback(async (notifyParent: boolean = false) => {
    if (!contractId || !projectId) return;

    try {
      setLoading(true);
      const response = await fetch(
        apiUrl(`/api/contracts/${contractId}/documents?project_id=${projectId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const docs = data.documents || [];
        setDocuments(docs);
        if (notifyParent) {
          onDocumentCountChange?.(docs.length);
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch documents');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [contractId, projectId, onDocumentCountChange]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
    setUploadData({
      document_type: 'contract',
      notes: '',
      expiry_date: ''
    });
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || uploading) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', uploadData.document_type);
      if (uploadData.notes) formData.append('notes', uploadData.notes);
      if (uploadData.expiry_date) formData.append('expiry_date', uploadData.expiry_date);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(
        apiUrl(`/api/contracts/${contractId}/documents?project_id=${projectId}`),
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        await fetchDocuments(true);
        handleUploadDialogClose();
        setSuccess('Document uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      // If it's an Azure URL, open directly
      if (document.file_path && document.file_path.includes('blob.core.windows.net')) {
        window.open(document.file_path, '_blank');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      setDeletingId(documentId);
      const response = await fetch(
        apiUrl(`/api/contracts/${contractId}/documents/${documentId}?project_id=${projectId}`),
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        await fetchDocuments(true);
        setSuccess('Document deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };


  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return <PdfIcon sx={{ color: '#D32F2F' }} />;
    if (mimeType?.includes('image')) return <ImageIcon sx={{ color: '#1976D2' }} />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <DocIcon sx={{ color: '#1565C0' }} />;
    return <FileIcon sx={{ color: '#757575' }} />;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      contract: '#2196F3',
      amendment: '#9C27B0',
      variation: '#FF9800',
      invoice: '#4CAF50',
      correspondence: '#00BCD4',
      insurance: '#E91E63',
      bond: '#795548',
      other: '#607D8B'
    };
    return colors[type] || '#607D8B';
  };

  const isExpiringSoon = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  const isPreviewable = (mimeType: string | null) => {
    if (!mimeType) return false;
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || // .docx
      mimeType === 'application/msword' // .doc
    );
  };

  const handlePreview = async (document: Document) => {
    try {
      setPreviewLoading(true);
      setPreviewDocument(document);
      setPreviewOpen(true);
      setWordHtml(null);
      setPreviewUrl(null);

      // If it's an Azure URL, use it directly for preview
      if (document.file_path && document.file_path.includes('blob.core.windows.net')) {
        // Check if it's a Word document
        const isWord = document.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                       document.mime_type === 'application/msword';

        if (isWord) {
          // For Word documents, fetch blob and convert to HTML
          const response = await fetch(document.file_path);
          if (response.ok) {
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            setWordHtml(result.value);
          } else {
            throw new Error('Failed to load preview');
          }
        } else {
          // For PDF and images, use Azure URL directly
          setPreviewUrl(document.file_path);
        }
      }
    } catch (err: any) {
      setError(err.message);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
    setWordHtml(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Contract Documents
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleUploadDialogOpen}
            sx={{ borderRadius: 2 }}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        </Fade>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>File</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Expiry</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Uploaded</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <DocumentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary" variant="body2">
                        No documents attached yet
                      </Typography>
                      {canWrite && (
                        <Button
                          size="small"
                          startIcon={<UploadIcon />}
                          onClick={handleUploadDialogOpen}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Upload your first document
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow
                      key={doc.document_id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:last-child td': { borderBottom: 0 }
                      }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getFileIcon(doc.mime_type)}
                          <Box>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 180 }}>
                              {doc.document_name}
                            </Typography>
                            {doc.notes && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                                {doc.notes}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={doc.document_type}
                          size="small"
                          sx={{
                            bgcolor: `${getTypeColor(doc.document_type)}15`,
                            color: getTypeColor(doc.document_type),
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(doc.file_size)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {doc.expiry_date ? (
                          <Chip
                            label={formatDate(doc.expiry_date)}
                            size="small"
                            color={
                              isExpired(doc.expiry_date)
                                ? 'error'
                                : isExpiringSoon(doc.expiry_date)
                                ? 'warning'
                                : 'default'
                            }
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(doc.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="Download">
                            <IconButton
                              onClick={() => handleDownload(doc)}
                              size="small"
                              color="primary"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canWrite && (
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDelete(doc.document_id)}
                                size="small"
                                color="error"
                                disabled={deletingId === doc.document_id}
                              >
                                {deletingId === doc.document_id ? (
                                  <CircularProgress size={18} color="error" />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Upload Contract Document</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Drag & Drop Zone */}
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: dragActive ? 'primary.50' : 'grey.50',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
              onClick={() => document.getElementById('contract-file-input')?.click()}
            >
              <input
                id="contract-file-input"
                type="file"
                hidden
                onChange={handleFileSelect}
              />
              {selectedFile ? (
                <Box>
                  <FileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" fontWeight={500}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    Drag & drop file here or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    PDF, Images, Word, Excel supported (max 10MB)
                  </Typography>
                </Box>
              )}
            </Box>

            {uploading && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ borderRadius: 1, height: 6 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <TextField
              select
              label="Document Type"
              value={uploadData.document_type}
              onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
              fullWidth
              size="small"
              helperText={`Supported formats: ${documentTypes.find(t => t.value === uploadData.document_type)?.formats || ''}`}
            >
              {documentTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{type.icon}</span>
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Expiry Date"
              type="date"
              value={uploadData.expiry_date}
              onChange={(e) => setUploadData({ ...uploadData, expiry_date: e.target.value })}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="Notes (optional)"
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Add any notes or description..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PreviewIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {previewDocument?.document_name || 'Document Preview'}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100' }}>
          {previewLoading ? (
            <CircularProgress />
          ) : wordHtml ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
                p: 4,
                bgcolor: 'white'
              }}
              dangerouslySetInnerHTML={{ __html: wordHtml }}
            />
          ) : previewUrl ? (
            previewDocument?.mime_type?.startsWith('image/') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={previewDocument?.document_name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : previewDocument?.mime_type === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                title={previewDocument?.document_name}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            ) : (
              <Typography color="text.secondary">
                Preview not available for this file type
              </Typography>
            )
          ) : (
            <Typography color="text.secondary">
              Loading preview...
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => previewDocument && handleDownload(previewDocument)}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractDocuments;
